import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Building2, Heart } from "lucide-react";
import { cn } from "@/lib/utils";

interface FooterLink {
  label: string;
  href: string;
}

interface SocialLink {
  icon: React.ReactNode;
  href: string;
  label: string;
}

interface FooterProps {
  brandName?: string;
  brandDescription?: string;
  socialLinks?: SocialLink[];
  navLinks?: FooterLink[];
  creatorName?: string;
  creatorUrl?: string;
  brandIcon?: React.ReactNode;
  className?: string;
}

const useFooterInView = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
};

const animStyle = (visible: boolean, delay: number, scale = false): React.CSSProperties => ({
  opacity: visible ? 1 : 0,
  transform: visible
    ? "translateY(0)" + (scale ? " scale(1)" : "")
    : "translateY(30px)" + (scale ? " scale(0.8)" : ""),
  transition: `opacity 700ms ease-out ${delay}ms, transform 700ms ease-out ${delay}ms`,
});

export const AnimatedFooter = ({
  brandName = "YourBrand",
  brandDescription = "Your description here",
  socialLinks = [],
  navLinks = [],
  creatorName,
  creatorUrl,
  brandIcon,
  className,
}: FooterProps) => {
  const isExternal = (href: string) => href.startsWith("http") || href.startsWith("mailto:");
  const { ref, isVisible } = useFooterInView();

  return (
    <footer ref={ref} className={cn("relative z-10 bg-slate-950 overflow-hidden", className)}>
      <div className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative z-10 flex flex-col items-center justify-center py-16 md:py-24">
            <div className="flex flex-col items-center text-center space-y-8 max-w-2xl">
              {/* Brand */}
              <div className="flex items-center gap-3" style={animStyle(isVisible, 0)}>
                {brandIcon ? (
                  brandIcon
                ) : (
                  <>
                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                      <Building2 className="h-6 w-6 text-white" />
                    </div>
                    <span className="text-2xl font-bold text-white tracking-tight">
                      {brandName}
                    </span>
                  </>
                )}
              </div>

              {/* Description */}
              <p className="text-slate-400 text-base leading-relaxed max-w-md" style={animStyle(isVisible, 150)}>
                {brandDescription}
              </p>

              {/* Social Links */}
              {socialLinks.length > 0 && (
                <div className="flex items-center gap-4" style={animStyle(isVisible, 300)}>
                  {socialLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative w-11 h-11 bg-slate-800/60 hover:bg-blue-600 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-blue-500/25"
                      aria-label={link.label}
                    >
                      <span className="text-slate-400 group-hover:text-white transition-colors duration-300">
                        {link.icon}
                      </span>
                    </a>
                  ))}
                </div>
              )}

              {/* Nav Links */}
              {navLinks.length > 0 && (
                <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2" style={animStyle(isVisible, 450)}>
                  {navLinks.map((link, index) =>
                    isExternal(link.href) ? (
                      <a
                        key={index}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-slate-500 hover:text-white transition-colors duration-300"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        key={index}
                        to={link.href}
                        className="text-sm text-slate-500 hover:text-white transition-colors duration-300"
                      >
                        {link.label}
                      </Link>
                    )
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Copyright */}
          <div className="relative z-10 flex flex-col sm:flex-row items-center justify-between gap-4 py-6 border-t border-slate-800/50" style={animStyle(isVisible, 600)}>
            <p className="text-slate-600 text-sm">
              ©{new Date().getFullYear()} {brandName}. Todos os direitos reservados.{" "}
              <span className="mx-4">|</span> Feito com <Heart className="inline w-4 h-4 text-red-500 fill-red-500 mx-1" /> Itaboraí
            </p>
            {creatorName && creatorUrl && (
              <p className="text-slate-600 text-sm">
                <a
                  href={creatorUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors duration-300"
                >
                  {creatorName}
                </a>
              </p>
            )}
          </div>
        </div>

        {/* Large background text */}
        <div
          className="absolute bottom-24 left-0 right-0 flex items-center justify-center pointer-events-none select-none"
          style={animStyle(isVisible, 300, true)}
        >
          <span className="text-[8rem] sm:text-[12rem] md:text-[16rem] lg:text-[20rem] font-black text-white/[0.06] leading-none tracking-tighter whitespace-nowrap">
            {brandName.toUpperCase()}
          </span>
        </div>

        {/* Bottom logo */}
        <div className="relative z-10 flex items-center justify-center pb-8" style={animStyle(isVisible, 750)}>
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center opacity-40">
            {brandIcon || <Building2 className="h-5 w-5 text-white" />}
          </div>
        </div>

        {/* Bottom decorative line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />

        {/* Bottom shadow */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-blue-950/20 to-transparent pointer-events-none" />
      </div>
    </footer>
  );
};
